import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    ResponsiveContainer,
    Tooltip,
    YAxis,
    XAxis,
    ReferenceLine,
} from 'recharts';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const EM_PURPLE = '#a855f7';

const PERIODS = [
    { id: 'today', label: 'Ditë' },
    { id: 'mtd', label: 'Muaj' },
    { id: 'ytd', label: 'Vit' },
    { id: 'total', label: 'Gjithë jeta' },
];

function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatYmd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTooltipLabel(tsMs) {
    return new Date(tsMs).toLocaleString('sq-AL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function toNumber(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}
function nonNeg(v) {
    return Math.abs(toNumber(v));
}
function formatMwh(v, d = 2) {
    return `${nonNeg(v).toFixed(d)} MWh`;
}
function formatMw(v, d = 2) {
    return `${nonNeg(v).toFixed(d)} MW`;
}

const PvFleetEnergyDashboard = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
    const [capacityMw, setCapacityMw] = useState(0);
    const [series, setSeries] = useState([]);
    const [fleetEnergyMwh, setFleetEnergyMwh] = useState(0);
    const [fleetPowerMw, setFleetPowerMw] = useState(0);
    const [monthToDateMwh, setMonthToDateMwh] = useState(0);
    const [energyUpdated, setEnergyUpdated] = useState(null);
    const [error, setError] = useState(null);
    const [isDark, setIsDark] = useState(
        () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
    );
    const mountedRef = useRef(false);

    const authHeaders = useCallback(
        () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }),
        [],
    );

    useEffect(() => {
        const el = document.documentElement;
        const sync = () => setIsDark(el.classList.contains('dark'));
        const obs = new MutationObserver(sync);
        obs.observe(el, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);

    const effectiveChartLimitMw = useMemo(() => {
        const cap = nonNeg(capacityMw);
        const maxData = series.reduce((m, p) => Math.max(m, nonNeg(p.pvMw)), 0);
        const fromSeries = maxData > 0 ? nonNeg(maxData * 1.08) : 0;
        if (cap > 0) return Math.max(cap, fromSeries, 0.001);
        return Math.max(fromSeries, 0.001);
    }, [capacityMw, series]);

    const dayLabel = useMemo(() => formatYmd(selectedDay), [selectedDay]);
    const chartData = useMemo(() => series.map((s) => ({ ...s, pvMw: nonNeg(s.pvMw) })), [series]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchDashboard = useCallback(async (isPoll = false) => {
        if (!isPoll) setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ period });
            if (period === 'today') params.set('day', formatYmd(selectedDay));
            const res = await axios.get(`${BACKEND_URL}/api/pv-fleet/energy-dashboard?${params}`, {
                headers: authHeaders(),
            });
            const d = res.data || {};
            if (!mountedRef.current) return;
            setFleetEnergyMwh(nonNeg(d.energy_produced_mwh));
            setFleetPowerMw(nonNeg(d.fleet_power_mw));
            setMonthToDateMwh(nonNeg(d.month_to_date_energy_mwh));
            setCapacityMw(nonNeg(d.capacity_mw));
            setSeries(Array.isArray(d.series) ? d.series : []);
            setEnergyUpdated(d.energy_updated_at || null);
        } catch (err) {
            console.error('PvFleetEnergyDashboard fetch error', err);
            if (mountedRef.current) setError('Nuk u ngarkuan të dhënat e flotës.');
        } finally {
            if (mountedRef.current && !isPoll) setLoading(false);
        }
    }, [period, selectedDay, authHeaders]);

    useEffect(() => {
        fetchDashboard(false);
    }, [fetchDashboard]);

    useEffect(() => {
        const id = setInterval(() => fetchDashboard(true), period === 'today' ? 60_000 : 120_000);
        return () => clearInterval(id);
    }, [fetchDashboard, period]);

    const shiftSelectedDay = (delta) =>
        setSelectedDay((d) => {
            const n = new Date(d);
            n.setDate(n.getDate() + delta);
            return startOfDay(n);
        });

    const gridStroke = isDark ? '#334155' : '#e5e7eb';
    const tickFill = isDark ? '#9ca3af' : '#6b7280';

    return (
        <Layout>
            <div className="flex min-h-0 flex-col overflow-hidden px-4 py-3 md:px-5 md:py-4 bg-gray-50 dark:bg-[#0f172a] h-[calc(100dvh-7.5rem)] md:h-[calc(100dvh-4.5rem)]">
                <div className="mb-2 shrink-0 flex items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="text-xs md:text-sm font-medium text-purple-700 dark:text-purple-300 hover:underline"
                    >
                        ← {t('pvFleetBackToDashboard')}
                    </button>
                </div>
                {error && (
                    <div className="mb-3 shrink-0 rounded-lg border border-red-300 bg-red-50 p-2.5 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-3 shrink-0">
                    <div className="rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-slate-700 p-2.5 sm:p-3 min-w-0">
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 leading-tight">Energjia prodhuar</p>
                        <p className="text-base sm:text-lg font-bold tabular-nums text-purple-600 dark:text-purple-400 truncate">
                            {formatMwh(fleetEnergyMwh, 2)}
                        </p>
                        {energyUpdated && (
                            <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 tabular-nums leading-tight line-clamp-2">
                                Përditësim: {new Date(energyUpdated).toLocaleString('sq-AL')}
                            </p>
                        )}
                    </div>
                    <div className="rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-slate-700 p-2.5 sm:p-3 min-w-0">
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 leading-tight">Duke prodhuar</p>
                        <p className="text-base sm:text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400 truncate">
                            {formatMw(fleetPowerMw, 2)}
                        </p>
                        <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-tight">Fuqia (flotë)</p>
                    </div>
                    <div className="rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-slate-700 p-2.5 sm:p-3 min-w-0">
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 leading-tight">Energjia mujore</p>
                        <p className="text-base sm:text-lg font-bold tabular-nums text-purple-600 dark:text-purple-400 truncate">
                            {formatMwh(monthToDateMwh, 2)}
                        </p>
                        <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-tight">1 → sot (muaji)</p>
                    </div>
                    <div className="rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-slate-700 p-2.5 sm:p-3 min-w-0">
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 leading-tight">Kapaciteti total</p>
                        <p className="text-base sm:text-lg font-bold tabular-nums text-amber-600 dark:text-amber-400 truncate">
                            {formatMw(capacityMw, 2)}
                        </p>
                        <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-tight">Instaluar</p>
                    </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-slate-700 p-4">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 shrink-0">Kurba në kohë reale</h2>

                    <div className="flex flex-wrap justify-center gap-1.5 mb-3 shrink-0">
                        {PERIODS.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setPeriod(p.id)}
                                className={`rounded-lg px-4 py-1.5 text-xs font-medium transition ${period === p.id
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-gray-300'
                                    }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {period === 'today' && (
                        <div className="flex items-center justify-center gap-2 mb-3 shrink-0">
                            <button
                                type="button"
                                onClick={() => shiftSelectedDay(-1)}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700"
                            >
                                ◀
                            </button>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-100 tabular-nums min-w-[6.5rem] text-center">
                                {dayLabel}
                            </span>
                            <button
                                type="button"
                                onClick={() => shiftSelectedDay(1)}
                                disabled={startOfDay(selectedDay).getTime() >= startOfDay(new Date()).getTime()}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-slate-700"
                            >
                                ▶
                            </button>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 mb-2 shrink-0 text-xs">
                        <span className="inline-block h-0.5 w-6 rounded-full" style={{ backgroundColor: EM_PURPLE }} />
                        <span className="text-gray-600 dark:text-gray-400">Fuqia (flotë)</span>
                    </div>

                    <div className="relative w-full flex-1 min-h-0 basis-0">
                        {loading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 dark:bg-[#0f172a]/80">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="h-7 w-7 animate-spin rounded-full border-2 border-purple-500 border-t-transparent" />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Duke ngarkuar…</span>
                                </div>
                            </div>
                        )}
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                                <defs>
                                    <linearGradient id="pvFleetEmFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={EM_PURPLE} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={EM_PURPLE} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    axisLine={false}
                                    tickLine={false}
                                    minTickGap={24}
                                    tick={{ fill: tickFill, fontSize: 10 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    width={52}
                                    domain={[0, Math.max(nonNeg(effectiveChartLimitMw), 0.001)]}
                                    tick={{ fill: tickFill, fontSize: 10 }}
                                    tickFormatter={(v) => `${nonNeg(v * 1000).toFixed(0)} kW`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: isDark ? '#1e293b' : '#fff',
                                        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                                        borderRadius: '8px',
                                        color: isDark ? '#fff' : '#000',
                                    }}
                                    labelFormatter={(_, p) => (p && p[0] ? formatTooltipLabel(p[0].payload.ts) : '')}
                                    formatter={(value) => [`${nonNeg(Number(value) * 1000).toFixed(2)} kW`, 'Fuqia']}
                                />
                                <ReferenceLine
                                    y={nonNeg(capacityMw)}
                                    stroke="#fb923c"
                                    strokeDasharray="5 5"
                                    strokeOpacity={0.85}
                                    ifOverflow="hidden"
                                />
                                <Area type="monotone" dataKey="pvMw" stroke="none" fill="url(#pvFleetEmFill)" isAnimationActive={false} />
                                <Line
                                    type="monotone"
                                    dataKey="pvMw"
                                    stroke={EM_PURPLE}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default PvFleetEnergyDashboard;
